// scopedChannel — one private Broadcast channel per topic per tab, ref-counted.
//
// Live data v2, lane 2 (REALTIME_REDESIGN_PLAN.md §3). The database publishes
// to a named PRIVATE topic (migration 0077); permission is checked by Realtime
// once when the channel is joined, never per message. This module owns the
// channel so that:
//   • two consumers of the same topic share ONE channel (supabase-js hands back
//     the same channel object for the same topic, so an un-counted
//     removeChannel by one consumer would silence the other);
//   • the channel exists only while somebody is mounted — the last leave
//     removes it;
//   • a REFUSED first join (migration not applied, role not allowed, private
//     channels unavailable) is reported once as `onUnavailable`, the channel is
//     removed — no rejoin loop against a topic that will keep refusing — and
//     the consumer falls back to its old path;
//   • a first join that only FAILED (timed out, socket lost, Realtime over its
//     limits) is not a refusal (Phase 1, 2026-09-30): the old path is whole-table
//     bindings, the most expensive thing to add while Realtime is struggling —
//     which is exactly when joins time out. The channel is closed (no 10-second
//     library rejoin loop), the consumers re-read the truth, and the join is tried
//     again after 15 s and 30 s; the late first join re-reads once more. After
//     JOIN_MAX_ATTEMPTS failures in a row it is reported unavailable like a refusal
//     (a group room has no poll of its own without chatParity — it must not lag for
//     minutes); a short blip no longer costs ten minutes of whole-table bindings;
//   • a RE-join after a drop asks every consumer to re-read the truth, through
//     the same jittered `channelResync` policy every other subscription uses.
//
// A broadcast is a hint, never the truth (LANE1_PLAN.md §0): consumers must be
// correct with every message lost, duplicated or out of order.
//
// Lane 1 builds its own `services/live/userChannel.ts`; the two are meant to be
// unified at merge time, which is why this file is deliberately small.
import { supabase, channelResync, RESYNC_JITTER_MS } from '../supabaseClient';

export interface ScopedListener {
  /** One broadcast message: its event name and payload. */
  onEvent: (event: string, payload: any) => void;
  /** The channel re-joined after a drop (or stayed down a long time): re-read the truth. */
  onResync: () => void;
  /** The FIRST join was refused or failed — the channel is gone; fall back. */
  onUnavailable: () => void;
  /** The first join succeeded. */
  onLive?: () => void;
  /** Every raw channel status, for consumers that keep their own liveness clock. */
  onStatus?: (status: string) => void;
}

interface TopicState {
  topic: string;
  events: string[];
  listeners: Set<ScopedListener>;
  channel: any | null;
  joined: boolean;
  dead: boolean;
  closing: ReturnType<typeof setTimeout> | null;
  /** First-join attempts that failed without a refusal, and the timer of the next one. */
  attempts: number;
  retry: ReturnType<typeof setTimeout> | null;
  /** Bumped per open, so a closed attempt's late status is ignored. */
  gen: number;
}

/** Waits before the next first-join attempt after a failure that was not a refusal (±20%). */
export const JOIN_RETRY_MS = [15_000, 30_000];
/** Failed first joins in a row (not refusals) before the topic is reported unavailable. */
export const JOIN_MAX_ATTEMPTS = 3;
// Only a permission answer is a refusal (audit, 2026-09-30: a list of "transient" words missed
// Realtime's own overload answers — "DatabaseLackOfConnections", "RealtimeRestarting", … — and sent
// them straight to the whole-table fallback). Everything else — the timeout, a lost socket, any other
// server answer — is retried, and after JOIN_MAX_ATTEMPTS it falls back all the same.
const REFUSED = /unauthori[sz]ed|permission|forbidden|not allowed|denied/i;
/** True when a first-join status is a real refusal rather than a failure worth retrying. */
export function isJoinRefusal(status: string, err?: unknown): boolean {
  if (status !== 'CHANNEL_ERROR') return false;
  if (err == null) return false;
  const msg = err instanceof Error ? err.message : String(err);
  return REFUSED.test(msg);
}

const topics = new Map<string, TopicState>();
// A topic that refused us is not asked again for a while: the usual reasons
// (migration not applied, role not allowed) do not change from one page visit
// to the next, and every attempt is a join against Realtime's joins-per-second
// budget.
const UNAVAILABLE_TTL_MS = 10 * 60 * 1000;
// The last leave removes the channel a moment LATER. supabase-js removes a
// channel asynchronously and hands back the same (closing) object to a join of
// the same topic in between, so leave-then-join in one breath — React
// StrictMode, a quick page flip — must reuse the live channel instead.
let leaveGraceMs = 1500;
/** Test seam. */
export function _setLeaveGraceMs(ms: number): void { leaveGraceMs = ms; }
const unavailableUntil = new Map<string, number>();

function teardown(st: TopicState): void {
  st.dead = true;
  if (st.closing) { clearTimeout(st.closing); st.closing = null; }
  if (st.retry) { clearTimeout(st.retry); st.retry = null; }
  topics.delete(st.topic);
  const ch = st.channel;
  st.channel = null;
  if (ch) { try { void supabase().removeChannel(ch); } catch { /* ignore */ } }
}

function fail(st: TopicState): void {
  if (st.dead) return;
  unavailableUntil.set(st.topic, Date.now() + UNAVAILABLE_TTL_MS);
  const ls = [...st.listeners];
  st.listeners.clear();
  teardown(st);
  ls.forEach((l) => { try { l.onUnavailable(); } catch { /* ignore */ } });
}

/** A first join failed without a refusal: close this attempt, keep the consumers, try again later. */
function retryLater(st: TopicState): void {
  if (st.dead) return;
  if (st.attempts + 1 >= JOIN_MAX_ATTEMPTS) { fail(st); return; }
  st.gen += 1; // the closed attempt's CLOSED status is not ours any more
  const ch = st.channel;
  st.channel = null;
  if (ch) { try { void supabase().removeChannel(ch); } catch { /* ignore */ } }
  const wait = JOIN_RETRY_MS[Math.min(st.attempts, JOIN_RETRY_MS.length - 1)] * (0.8 + Math.random() * 0.4);
  st.attempts += 1;
  if (st.retry) clearTimeout(st.retry);
  st.retry = setTimeout(() => {
    st.retry = null;
    if (st.dead) return;
    // Nothing reached us since the last read: re-read, then knock again.
    st.listeners.forEach((l) => { try { l.onResync(); } catch { /* ignore */ } });
    void open(st);
  }, wait);
}

async function open(st: TopicState): Promise<void> {
  const gen = ++st.gen;
  try {
    // A private channel is authorised with the user's token. supabase-js keeps
    // it fresh afterwards; this makes sure the FIRST join carries it.
    try { await supabase().realtime?.setAuth?.(); } catch { /* the join below decides */ }
    if (st.dead || gen !== st.gen) return;
    let ch = supabase().channel(st.topic, { config: { private: true } });
    for (const ev of st.events) {
      ch = ch.on('broadcast', { event: ev }, (msg: any) => {
        if (st.dead) return;
        st.listeners.forEach((l) => { try { l.onEvent(msg?.event ?? ev, msg?.payload); } catch { /* one bad consumer must not starve the rest */ } });
      });
    }
    st.channel = ch;
    const resync = channelResync(() => {
      if (st.dead) return;
      st.listeners.forEach((l) => { try { l.onResync(); } catch { /* ignore */ } });
    });
    ch.subscribe((status: string, err?: unknown) => {
      if (st.dead || gen !== st.gen) return;
      st.listeners.forEach((l) => { try { l.onStatus?.(status); } catch { /* ignore */ } });
      if (status === 'SUBSCRIBED') {
        const first = !st.joined;
        st.joined = true;
        resync(status);
        if (first) st.listeners.forEach((l) => { try { l.onLive?.(); } catch { /* ignore */ } });
        if (first && st.attempts > 0) {
          // Joined late: hints sent since the last re-read never reached us.
          st.attempts = 0;
          const delay = RESYNC_JITTER_MS[0] + Math.random() * (RESYNC_JITTER_MS[1] - RESYNC_JITTER_MS[0]);
          st.retry = setTimeout(() => {
            st.retry = null;
            if (!st.dead) st.listeners.forEach((l) => { try { l.onResync(); } catch { /* ignore */ } });
          }, delay);
        }
        return;
      }
      if (!st.joined && (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT')) {
        if (isJoinRefusal(status, err)) fail(st); else retryLater(st);
        return;
      }
      resync(status);
    });
  } catch {
    fail(st);
  }
}

/**
 * Join `topic` (private) for the named broadcast events. Returns leave().
 * Every caller for the same topic must pass the same `events`.
 */
export function joinScopedChannel(topic: string, events: string[], listener: ScopedListener): () => void {
  if ((unavailableUntil.get(topic) ?? 0) > Date.now()) {
    let cancelled = false;
    // Async like a real refusal, so callers have one code path.
    void Promise.resolve().then(() => { if (!cancelled) listener.onUnavailable(); });
    return () => { cancelled = true; };
  }
  let st = topics.get(topic);
  if (!st) {
    st = { topic, events, listeners: new Set(), channel: null, joined: false, dead: false, closing: null, attempts: 0, retry: null, gen: 0 };
    topics.set(topic, st);
    st.listeners.add(listener);
    void open(st);
  } else {
    if (st.closing) { clearTimeout(st.closing); st.closing = null; }
    st.listeners.add(listener);
    if (st.joined) { const l = listener; void Promise.resolve().then(() => { if (st!.listeners.has(l)) l.onLive?.(); }); }
  }
  const mine = st;
  return () => {
    if (!mine.listeners.delete(listener)) return;
    if (mine.listeners.size > 0 || mine.dead) return;
    if (leaveGraceMs <= 0) { teardown(mine); return; }
    if (mine.closing) clearTimeout(mine.closing);
    mine.closing = setTimeout(() => {
      mine.closing = null;
      if (mine.listeners.size === 0 && !mine.dead) teardown(mine);
    }, leaveGraceMs);
  };
}

/** Test seam: forget every topic and every remembered refusal. */
export function _resetScopedChannels(): void {
  [...topics.values()].forEach(teardown);
  topics.clear();
  unavailableUntil.clear();
}
